import { Drawer, Grid, Select, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';
import { useDebouncedState } from '@mantine/hooks';
import { DebtorSelect } from 'components/DebtorSelect';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales';
import type { MailsSearchParams } from 'modules/Mails/hooks/use-load-mails';
import { MAIL_STATUSES } from 'modules/Mails/types/email-status';
import { useState } from 'react';
import { capitalize } from 'remeda';
import { useLoadExternalMailboxes } from 'shared/hooks/use-load-external-mailboxes';
import { useLoadLightUsers } from 'shared/hooks/use-load-light-users';

type Option = { value: string; label: string };

type MailFilterDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  form: UseFormReturnType<MailsSearchParams>;
};

export const MailFilterDrawer = ({ isOpen, onClose, form }: MailFilterDrawerProps) => {
  const { t } = useTranslation();
  const profile = useProfile();

  const { externalMailboxes } = useLoadExternalMailboxes();

  const [userSearchText, setUserSearchText] = useDebouncedState('', 200);
  const [selectedUser, setSelectedUser] = useState<Option | null>(null);

  const shouldLoadUsers = profile.isAdmin || profile.roles.includes('all_invoices');
  const { users } = useLoadLightUsers(
    false,
    { query: userSearchText },
    { isEnabled: shouldLoadUsers },
  );

  const userOptions = users.map(u => ({ value: String(u.id), label: u.name.full }));
  if (selectedUser && !userOptions.some(u => u.value === selectedUser.value))
    userOptions.unshift(selectedUser);

  const statusOptions = MAIL_STATUSES.map(value => ({
    value,
    label: t(tKeysLoose.MAILS.STATUS[value.toUpperCase()]),
  }));

  const mailboxOptions = externalMailboxes.data.map(mailbox => ({
    value: mailbox.id,
    label: mailbox.attributes.login || mailbox.attributes.host || '',
  }));

  return (
    <Drawer position="right" opened={isOpen} onClose={onClose} title={t(i18nKeys.FILTERS)}>
      <Grid>
        <Grid.Col span={12}>
          <TextInput
            label={t(i18nKeys.SUBJECT)}
            placeholder={t(i18nKeys.SUBJECT)}
            {...form.getInputProps('subject')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={t(i18nKeys.BODY)}
            placeholder={t(i18nKeys.BODY)}
            {...form.getInputProps('body')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={capitalize(t(i18nKeys.FROM))}
            placeholder={capitalize(t(i18nKeys.FROM))}
            {...form.getInputProps('fromMail')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Select
            label={t(i18nKeys.TASK.ATTRIBUTES.STATUS)}
            placeholder={t(i18nKeys.TASK.ATTRIBUTES.STATUS)}
            data={statusOptions}
            clearable
            {...form.getInputProps('status')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <DebtorSelect
            value={form.values.debtorId || null}
            onChange={debtorId => form.setFieldValue('debtorId', debtorId ?? '')}
          />
        </Grid.Col>
        {profile.isAdmin && (
          <Grid.Col span={12}>
            <Select
              label={t(i18nKeys.ACCOUNT_MANAGER)}
              placeholder={t(i18nKeys.ACCOUNT_MANAGER)}
              data={userOptions}
              searchable
              clearable
              onSearchChange={setUserSearchText}
              value={form.values.accountManagerId || null}
              onChange={value => {
                form.setFieldValue('accountManagerId', value ?? '');
                const found = users.find(u => String(u.id) === value);
                setSelectedUser(found ? { value: String(found.id), label: found.name.full } : null);
              }}
            />
          </Grid.Col>
        )}
        {mailboxOptions.length > 1 && (
          <Grid.Col span={12}>
            <Select
              label={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.TITLE)}
              placeholder={t(i18nKeys.SETTINGS.EXTERNAL_MAILBOX.TITLE)}
              data={mailboxOptions}
              clearable
              {...form.getInputProps('externalMailboxId')}
            />
          </Grid.Col>
        )}
        <Grid.Col span={6}>
          <DatePickerInput
            label={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.SENT_AFTER)}
            placeholder={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.SENT_AFTER)}
            allowDeselect
            clearable
            {...form.getInputProps('dateAfter')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <DatePickerInput
            label={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.SENT_BEFORE)}
            placeholder={t(i18nKeys.REMINDER_LIST.FILTERS.LABELS.SENT_BEFORE)}
            allowDeselect
            clearable
            {...form.getInputProps('dateBefore')}
          />
        </Grid.Col>
      </Grid>
    </Drawer>
  );
};
